import React, { useState, useCallback } from "react";
import { Flex, Text, Divider, Card, Grid, Skeleton, Loader } from "@mantine/core";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { CardContainerRetro } from "../Container/CardContainerRetro";
import { IconClock, IconNote } from "@tabler/icons-react";
import colorsCustom from "../../designTokens/colorsCustom";
import { useTranslation } from "react-i18next";

const ScheduledPostsList = ({ 
  scheduledPosts, 
  loadingPosts, 
  openEditModal, // 호환성을 위해 유지하지만 사용하지 않음
  drafts = [],
  loadingDrafts = false,
  onLoadDraft, // 호환성을 위해 유지하지만 사용하지 않음
  onDeleteDraft,
  onDeleteScheduledPost // 예약글 삭제 콜백 추가
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 선택 팝업 상태
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(''); // 'scheduled' | 'draft'
  
  console.log('[ScheduledPostsList] Props:', { 
    draftsCount: drafts.length, 
    scheduledPostsCount: scheduledPosts.length 
  });

  // 카드 클릭 핸들러 - 선택 팝업 열기
  const handleCardClick = (item, type) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setActionDialogOpen(true);
  };

  // 수정 선택 시
  const handleEditAction = () => {
    setActionDialogOpen(false);
    if (selectedItemType === 'scheduled') {
      navigate(`/post-thread/scheduled/${selectedItem.id}`);
    } else if (selectedItemType === 'draft') {
      navigate(`/post-thread/draft/${selectedItem.id}`);
    }
  };

  // 삭제 선택 시
  const handleDeleteAction = () => {
    setActionDialogOpen(false);
    if (selectedItemType === 'draft' && onDeleteDraft) {
      onDeleteDraft(selectedItem.id);
    } else if (selectedItemType === 'scheduled' && onDeleteScheduledPost) {
      onDeleteScheduledPost(selectedItem.id);
    }
  };

  // 팝업 닫기
  const handleCloseDialog = () => {
    setActionDialogOpen(false);
    setSelectedItem(null);
    setSelectedItemType('');
  };

  const summarizeReply = useCallback((reply) => {
    if (!reply) return '';
    if (typeof reply === 'string') return reply;

    const text = typeof reply.text === 'string' ? reply.text.trim() : '';
    if (text) return text;

    const mediaCount = Array.isArray(reply.media) ? reply.media.length : 0;
    if (mediaCount > 1) {
      return t('postThreads.replyPreviewMediaOnlyWithCount', { count: mediaCount });
    }
    if (mediaCount === 1) {
      return t('postThreads.replyPreviewMediaOnly');
    }
    return '';
  }, [t]);
  
  // 임시저장 게시물 렌더링 함수
  const renderDraftCard = (draft) => {
    const isTemporary = draft.isTemporary || false;
    return (
      <Grid.Col key={draft.id} span={4}>
        <CardContainerRetro
          w={"100%"}
          rd={12}
          borderType={"full"}
          onClick={isTemporary ? undefined : () => handleCardClick(draft, 'draft')}
          style={{ cursor: isTemporary ? "default" : "pointer", minHeight: 220, position: 'relative', opacity: isTemporary ? 0.7 : 1 }}
        >
          <Flex
            p={"20px 20px 16px 20px"}
            w={"100%"}
            bg={colorsCustom.paper}
            direction={"column"}
            gap={24}
            br={4}
          >
            <Flex direction={"row"} justify={"space-between"} align={"center"} h={28}>
              <Flex gap={8} align={"center"}>
                <IconNote size={22} color={colorsCustom.ui.dark.moderate} />
                <Text size="lg" fw={700} lh={1} c={"gray.4"}>
                  {t("postThreads.draft.label")}
                </Text>
              </Flex>
              <Flex gap={4} direction={"row"} align={"center"}>
                <Text size="sm" c="dimmed" fw={600}>
                  {format(draft.updatedAt, "MM/dd HH:mm", { locale: ko })}
                </Text>
              </Flex>
            </Flex>
            <Text
              ff={'"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif'}
              lh={1.55}
              size="2xl"
              h={64}
              fw={900}
              w="100%"
              c={colorsCustom.ui.dark.bold}
              style={{ overflow: "hidden" }}
            >
              {draft.title}
            </Text>
            {/* 스레드 개수 정보 */}
            <Flex
              gap={8}
              c={colorsCustom.ui.dark.moderate}
              wrap={"wrap"}
              justify={"space-between"}
              align={"center"}
            >
              <Text size="md" c="dimmed" fw={600}>
                {t("postThreads.draft.threadCount", { count: draft.threads?.length || 0 })}
                {draft.mediaFiles?.length > 0 && ` • ${t("postThreads.draft.mediaCount", { count: draft.mediaFiles.length })}`}
              </Text>
              <Text size="md" c="dimmed">
                {t("postThreads.draft.lastModified", { date: format(draft.updatedAt, "yyyy-MM-dd HH:mm", { locale: ko }) })}
              </Text>
            </Flex>
          </Flex>

          {/* 임시 드래프트인 경우 로딩 오버레이 표시 */}
          {isTemporary && (
            <Flex
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                borderRadius: 12,
                zIndex: 1
              }}
              justify="center"
              align="center"
            >
              <Flex direction="column" align="center" gap={8}>
                <Loader size="md" />
                <Text size="sm" c="dimmed" fw={600}>
                  {t("postThreads.draft.savingInProgress")}
                </Text>
              </Flex>
            </Flex>
          )}
        </CardContainerRetro>
      </Grid.Col>
    );
  };

  // 예약 게시물 렌더링 함수
  const renderScheduledCard = (post, index) => {
    const taskId = post.id || `task_${index}`;
    const scheduleTime = post.executeAt 
      ? new Date(post.executeAt + ' GMT+0900')
      : new Date();
    const createTime = post.scheduledAt 
      ? new Date(post.scheduledAt)
      : (post.createdAt ? new Date(post.createdAt) : new Date());
    const postText = post.text || t("postThreads.draft.defaultContent");
    const userID = post.userID || t("postThreads.draft.unknown");
    const replies = post.replies || [];
    const isTemporary = post.isTemporary || false;
    const firstReplySnippet = replies.length > 0 ? summarizeReply(replies[0]) : '';
    const moreRepliesLabel = replies.length > 1 ? t("postThreads.andMore", { count: replies.length - 1 }) : '';
    const replyPreviewText = (firstReplySnippet || moreRepliesLabel)
      ? t("postThreads.replyPreview", { firstReplySnippet, more: moreRepliesLabel })
      : '';

    return (
      <Grid.Col key={taskId} span={4}>
        <CardContainerRetro
          w={"100%"}
          rd={12}
          borderType={"full"}
          onClick={isTemporary ? undefined : () => handleCardClick({
            id: taskId,
            text: postText,
            executeAt: scheduleTime,
            replies: replies,
            userID: userID,
            media: post.media,
            collectionId: post.collectionId,
            createNewCollection: post.createNewCollection,
            autoAddToCollection: post.autoAddToCollection
          }, 'scheduled')}
          style={{ 
            cursor: isTemporary ? "default" : "pointer", 
            minHeight: 220,
            position: "relative",
            opacity: isTemporary ? 0.7 : 1
          }}
        >
          <Flex
            p={"20px 20px 16px 20px"}
            w={"100%"}
            bg={colorsCustom.paper}
            direction={"column"}
            gap={24}
            br={4}
          >
            <Flex direction={"row"} justify={"space-between"} align={"center"} h={28}>
              <Flex gap={8} align={"center"}>
                <IconClock size={22} color={colorsCustom.ui.dark.moderate} />
                <Text size="lg" fw={700} lh={1} c={"gray.4"}>
                  {format(scheduleTime, "MM/dd", { locale: ko })}
                </Text>
                <Text size="lg" fw={700} lh={1} c={"gray.4"}>
                  {format(scheduleTime, "EEE", { locale: ko })}
                </Text>
                <Text size="lg" fw={700} lh={1} c={"gray.4"}>
                  {format(scheduleTime, "HH:mm", { locale: ko })}
                </Text>
              </Flex>
              <Flex gap={4} direction={"row"} align={"center"}>
                <Text size="lg" c="dimmed" fw={700}>
                  {t(`postThreads.status.${post.status || 'scheduled'}`)}
                </Text>
              </Flex>
            </Flex>
            <Text
              ff={'"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif'}
              lh={1.55}
              size="2xl"
              h={64}
              fw={900}
              w="100%"
              c={colorsCustom.ui.dark.bold}
              style={{ overflow: "hidden" }}
            >
              {postText.split(".")[0].replace(/�/g, "")}
            </Text>
            {replies && replies.length > 0 && (
              <Flex
                gap={8}
                c={colorsCustom.ui.dark.moderate}
                wrap={"wrap"}
              >
                <Text size="md" c="dimmed" fw={600}>
                  {t("postThreads.autoReplyCount", { count: replies.length })}
                </Text>
                {replyPreviewText && (
                  <Text size="md" lineClamp={1} c="gray.6">
                    {replyPreviewText}
                  </Text>
                )}
              </Flex>
            )}
            <Text size="md" c="dimmed">
              {t("postThreads.createdAt", { date: format(createTime, "MM/dd HH:mm", { locale: ko }) })}
            </Text>
          </Flex>
          
          {/* 임시 게시물인 경우 로딩 오버레이 표시 */}
          {isTemporary && (
            <Flex
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                borderRadius: 12,
                zIndex: 1
              }}
              justify="center"
              align="center"
            >
              <Flex direction="column" align="center" gap={8}>
                <Loader size="md" />
                <Text size="sm" c="dimmed" fw={600}>
                  {t("postThreads.draft.schedulingInProgress")}
                </Text>
              </Flex>
            </Flex>
          )}
        </CardContainerRetro>
      </Grid.Col>
    );
  };
  
  // 단일 패널 구성으로 전환되어 외부 탭 변경은 사용하지 않음

  // 로딩 스켈레톤 카드 렌더링 함수
  const renderSkeletonCard = (index) => {
    return (
      <Grid.Col key={`skeleton-${index}`} span={4}>
        <CardContainerRetro
          w={"100%"}
          rd={12}
          borderType={"full"}
          style={{ minHeight: 220 }}
        >
          <Flex
            p={"20px 20px 16px 20px"}
            w={"100%"}
            bg={colorsCustom.paper}
            direction={"column"}
            gap={24}
            br={4}
          >
            {/* 헤더 스켈레톤 */}
            <Flex direction={"row"} justify={"space-between"} align={"center"} h={28}>
              <Flex gap={8} align={"center"}>
                <Skeleton height={22} width={22} radius="sm" />
                <Skeleton height={20} width={80} />
                <Skeleton height={20} width={40} />
                <Skeleton height={20} width={60} />
              </Flex>
              <Skeleton height={20} width={60} />
            </Flex>
            
            {/* 메인 텍스트 스켈레톤 */}
            <Flex direction="column" gap={8}>
              <Skeleton height={32} width="90%" />
              <Skeleton height={32} width="75%" />
            </Flex>
            
            {/* 푸터 스켈레톤 */}
            <Flex
              gap={8}
              wrap={"wrap"}
              justify={"space-between"}
              align={"center"}
            >
              <Skeleton height={20} width={120} />
              <Skeleton height={20} width={150} />
            </Flex>
          </Flex>
        </CardContainerRetro>
      </Grid.Col>
    );
  };

  return (
    <>
      <Divider style={{ width: "100%", margin: "16px 0 8px 0" }} />
      
      <Box sx={{ width: "100%" }}>
        {/* 단일 패널: 예약/임시 두 영역을 순차 표시 */}
        <Box style={{ paddingTop: 8, paddingBottom: 8 }}>
          <Text size="xl" fw={800} c={"gray.6"}>
            {t('postThreads.savedSection.scheduledTitle', { defaultValue: '예약 대기 게시물' })}
          </Text>
        </Box>

        <Box sx={{ pt: 1 }}>
          {loadingPosts ? (
            <Grid gutter={16} style={{ width: "100%" }}>
              {[0, 1, 2].map(renderSkeletonCard)}
            </Grid>
          ) : scheduledPosts.length > 0 ? (
            <Grid gutter={16} style={{ width: "100%" }}>
              {scheduledPosts.map(renderScheduledCard)}
            </Grid>
          ) : (
            <Card
              shadow="sm"
              padding="xl"
              radius="md"
              withBorder
              style={{ 
                width: "100%",
                textAlign: "center",
                backgroundColor: "#f8f9fa"
              }}
            >
              <Text c="dimmed" size="lg">
                {t("postThreads.noScheduledThreads")}
              </Text>
              <Text c="dimmed" size="md" mt={4}>
                {t("postThreads.noScheduledThreadsSub")}
              </Text>
            </Card>
          )}
        </Box>

        <Divider style={{ width: "100%", margin: "16px 0" }} />

        <Box style={{ paddingTop: 8, paddingBottom: 8 }}>
          <Text size="xl" fw={800} c={"gray.6"}>
            {t('postThreads.savedSection.draftsTitle', { defaultValue: '임시 저장 게시물' })}
          </Text>
        </Box>

        <Box sx={{ pt: 1 }}>
          {loadingDrafts ? (
            <Grid gutter={16} style={{ width: "100%" }}>
              {[0, 1, 2].map(renderSkeletonCard)}
            </Grid>
          ) : drafts.length > 0 ? (
            <Grid gutter={16} style={{ width: "100%" }}>
              {drafts.map(renderDraftCard)}
            </Grid>
          ) : (
            <Card
              shadow="sm"
              padding="xl"
              radius="md"
              withBorder
              style={{ 
                width: "100%",
                textAlign: "center",
                backgroundColor: "#f8f9fa"
              }}
            >
              <Text c="dimmed" size="lg">
                {t("postThreads.draft.noDrafts")}
              </Text>
              <Text c="dimmed" size="md" mt={4}>
                {t("postThreads.draft.noDraftsSub")}
              </Text>
            </Card>
          )}
        </Box>
      </Box>

      {/* 수정/삭제 선택 팝업 */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="action-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="action-dialog-title">
          {t('postThreads.draft.actionDialog.title', { 
            type: selectedItemType === 'scheduled' ? t('postThreads.draft.actionDialog.scheduledPost') : t('postThreads.draft.actionDialog.draftPost')
          })}
        </DialogTitle>
        <DialogContent>
          <Text size="md" style={{ marginBottom: '16px' }}>
            {t('postThreads.draft.actionDialog.message', { 
              item: selectedItem?.title || selectedItem?.text?.split('.')[0] || t('postThreads.draft.actionDialog.selectedItem')
            })}
          </Text>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={handleCloseDialog} color="inherit">
            {t('postThreads.draft.actionDialog.cancel')}
          </Button>
          <Button onClick={handleDeleteAction} color="error" variant="outlined">
            {t('postThreads.draft.actionDialog.delete')}
          </Button>
          <Button onClick={handleEditAction} color="primary" variant="contained">
            {t('postThreads.draft.actionDialog.edit')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScheduledPostsList; 
